import {
	arrow,
	autoPlacement,
	autoUpdate,
	FloatingArrow,
	FloatingPortal,
	offset,
	OffsetOptions,
	Placement,
	Strategy,
	useFloating,
} from '@floating-ui/react';
import { mergeRefs } from '@react-aria/utils';
import { cloneElement, FC, JSX, MouseEvent, ReactElement, ReactNode, Ref, useId, useRef, useState } from 'react';

import { lightArrowProps } from '../../util/floating';
import { Text } from '../Typography/Text/Text';
import { StyledTooltip } from './Tooltip.styles';

export interface TooltipProps {
	/**
	 * Visually hidden text for screen readers
	 */
	accessibilityLabel?: string;
	/**
	 * Toggle whether the tooltip is visible
	 */
	active?: boolean;
	/**
	 * Toggle whether the tooltip is enabled
	 */
	enabled?: boolean;
	/**
	 * The react component to use as the
	 * trigger for the tooltip
	 */
	children: ReactElement;
	/**
	 * The content of the tooltip
	 */
	content: ReactNode;
	/**
	 * If `true`, the tooltip will show an arrow tip
	 */
	hasArrow?: boolean;
	/** Placement */
	placement?: Placement;
	/**
	 * The placements the tooltip may take; it takes the one with the most room. Leave it out for any placement.
	 * `placement` alone does not fix the side, because the tooltip places itself automatically.
	 */
	allowedPlacements?: Placement[];
	/** `dark` (default), or `light`: a white panel with dark text, like Popover content */
	variant?: 'dark' | 'light';
	/** Strategy */
	strategy?: Strategy;
	/**
	 * The offset of the tooltip
	 */
	offset?: OffsetOptions;
	/**
	 * Override the tooltip's stacking order. Defaults to the standard popover
	 */
	zIndex?: number;
}

export const Tooltip: FC<TooltipProps> = ({
	accessibilityLabel,
	active = false,
	enabled = true,
	children,
	content,
	hasArrow,
	placement = 'bottom',
	allowedPlacements,
	variant = 'dark',
	strategy = 'absolute',
	offset: offsetOptions = { mainAxis: 10, crossAxis: 0 },
	zIndex,
	...rest
}) => {
	const htmlId = useId();

	const arrowRef = useRef(null);

	const [isInTrigger, setInTrigger] = useState<boolean>(false);
	const [isInTooltip, setInTooltip] = useState<boolean>(false);
	const [isActive, setActive] = useState<boolean>(active);

	const enterTrigger = () => {
		setInTrigger(true);
		setActive(true);
	};

	const leaveTrigger = () => {
		setInTrigger(false);
		setActive(isInTooltip || active);
	};

	const enterTooltip = () => {
		setInTooltip(true);
		setActive(true);
	};

	const leaveTooltip = () => {
		setInTooltip(false);
		setActive(isInTrigger || active);
	};

	const { floatingStyles, refs, context } = useFloating({
		placement,
		strategy,
		middleware: [
			autoPlacement(allowedPlacements ? { allowedPlacements } : undefined),
			arrow({ element: arrowRef }),
			offset(offsetOptions),
		],
		// The panel stays mounted while hidden. Track scroll and resize only while it shows: a hidden tooltip in every
		// table row would otherwise reposition on each layout change.
		whileElementsMounted: isActive ? autoUpdate : undefined,
	});

	const childProps = children.props as {
		ref?: Ref<Element>;
		onMouseEnter?: (event: MouseEvent) => void;
		onMouseLeave?: (event: MouseEvent) => void;
	};

	const trigger: ReactElement = cloneElement(children, {
		ref: mergeRefs(refs.setReference, childProps.ref),
		onMouseEnter: (event: MouseEvent) => {
			childProps.onMouseEnter?.(event);
			enterTrigger();
		},
		onMouseLeave: (event: MouseEvent) => {
			childProps.onMouseLeave?.(event);
			leaveTrigger();
		},
		'aria-describedby': content && enabled ? htmlId : undefined,
	});

	if (!enabled) return trigger;

	return (
		<>
			{trigger}
			{content && (
				<FloatingPortal>
					<StyledTooltip
						$variant={variant}
						ref={refs.setFloating}
						style={{
							...floatingStyles,
							visibility: isActive ? 'visible' : 'hidden',
							opacity: isActive ? 1 : 0,
							...(zIndex !== undefined ? { zIndex } : {}),
						}}
						role="tooltip"
						id={htmlId}
						aria-label={accessibilityLabel}
						{...(!isActive ? { 'aria-hidden': true } : {})}
						onMouseEnter={enterTooltip}
						onMouseLeave={leaveTooltip}
						{...rest}
					>
						<Text color={variant === 'light' ? 'gray' : 'white'} size="medium" weight="normal">
							{content}
						</Text>
						{hasArrow && (
							<FloatingArrow
								ref={arrowRef}
								context={context}
								{...(variant === 'light' ? lightArrowProps : {})}
							/>
						)}
					</StyledTooltip>
				</FloatingPortal>
			)}
		</>
	);
};

type WithTooltipProps = Omit<TooltipProps, 'content' | 'children'>;

export const withTooltip = (
	children: JSX.Element,
	content: string,
	enabled = true,
	tooltipProps?: WithTooltipProps
) => {
	const { hasArrow = true, placement = 'bottom', strategy = 'absolute', ...rest } = tooltipProps || {};
	return !enabled ? (
		children
	) : (
		<Tooltip content={content} hasArrow={hasArrow} strategy={strategy} placement={placement} {...rest}>
			{children}
		</Tooltip>
	);
};
